<app-header></app-header>

<app-loader
  [show]="isLoading"
  [mainMessage]="'QUOTATIONS.LOADING_POLICIES' | translate"
  [subMessage]="'QUOTATIONS.LOADING_POLICIES_SUB' | translate"
>
</app-loader>

<div class="flex flex-col min-h-screen bg-gray-50 font-sans">
  <main class="flex-grow w-full pb-10">
    <div class="flex">
      <aside class="w-[20%] flex-shrink-0">
        <app-stepper [currentStep]="activeStep"></app-stepper>
      </aside>

      <main class="flex-1 overflow-y-auto">
        <div class="max-w-full mx-5 p-8">
          <div class="mb-8">
            <h1 class="text-3xl font-bold text-gray-900 mb-2">{{ 'QUOTATIONS.SELECT_QUOTE' | translate }}</h1>
            <p class="text-gray-600">
              {{ 'QUOTATIONS.PAGE_DESC' | translate }}
            </p>
          </div>

          <div
            *ngIf="!isLoading && !error && quotes.length > 0"
            class="mb-6 flex items-center justify-between"
          >
            <div class="flex items-center gap-4">
              <span class="text-sm font-medium text-gray-700">
                {{ quotes.length }}
                {{ quotes.length === 1 ? ('QUOTATIONS.QUOTE' | translate) : ('QUOTATIONS.QUOTES' | translate) }} {{ 'QUOTATIONS.AVAILABLE' | translate }}
              </span>
            </div>

            <div class="flex items-center gap-3">
              <label for="sortSelect" class="text-sm font-medium text-gray-700">
                {{ 'QUOTATIONS.SORT_BY' | translate }}
              </label>
              <select
                id="sortSelect"
                (change)="onSortChange($event)"
                class="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white cursor-pointer"
              >
                <option
                  *ngFor="let option of sortOptions"
                  [value]="option.value + '-' + option.direction"
                  [selected]="
                    currentSortBy === option.value &&
                    currentSortDirection === option.direction
                  "
                >
                  {{ option.label }}
                </option>
              </select>

              <button
                (click)="refreshQuotes()"
                class="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                [title]="'QUOTATIONS.REFRESH_QUOTES' | translate"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  ></path>
                </svg>
                {{ 'QUOTATIONS.REFRESH' | translate }}
              </button>
            </div>
          </div>

          <div
            *ngIf="isLoading"
            class="flex flex-col items-center justify-center py-20"
          >
            <div class="relative">
              <div
                class="animate-spin rounded-full h-16 w-16 border-4 border-teal-200"
              ></div>
              <div
                class="animate-spin rounded-full h-16 w-16 border-t-4 border-teal-600 absolute top-0 left-0"
              ></div>
            </div>
            <div class="mt-6 text-center">
              <p class="text-lg font-medium text-gray-700">
                {{ 'QUOTATIONS.LOADING_QUOTES' | translate }}
              </p>
              <p class="text-sm text-gray-500 mt-1">
                {{ 'QUOTATIONS.FINDING_BEST_OPTIONS' | translate }}
              </p>
            </div>
          </div>

          <div *ngIf="error && !isLoading" class="mb-8">
            <div class="bg-red-50 border-l-4 border-red-400 rounded-lg p-6">
              <div class="flex items-center">
                <svg
                  class="w-6 h-6 text-red-400 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  ></path>
                </svg>
                <div class="flex-1">
                  <h3 class="text-red-800 font-medium">{{ 'QUOTATIONS.SOMETHING_WENT_WRONG' | translate }}</h3>
                  <p class="text-red-700 text-sm mt-1">{{ error }}</p>
                </div>
              </div>
            </div>
          </div>

          <div *ngIf="!isLoading && !error" class="space-y-8">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div
                *ngFor="let quote of quotes; let i = index"
                class="bg-white rounded-xl border-2 transition-all cursor-pointer hover:shadow-lg"
                [class.border-teal-500]="
                  selectedQuote?.quoteResponseId === quote.quoteResponseId
                "
                [class.border-gray-200]="
                  selectedQuote?.quoteResponseId !== quote.quoteResponseId
                "
                [class.shadow-md]="
                  selectedQuote?.quoteResponseId === quote.quoteResponseId
                "
              >
                <div class="p-6 border-b border-gray-100">
                  <div class="flex items-start justify-between mb-4">
                    <div class="flex items-center">
                      <div
                        class="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold mr-3"
                        [ngClass]="getProviderColor(quote.providerName)"
                      >
                        {{ getProviderInitials(quote.providerName) }}
                      </div>
                      <div>
                        <h3 class="font-bold text-gray-900">
                          {{ quote.providerName }}
                        </h3>
                        <p class="text-xs text-gray-500">
                          {{ 'QUOTATIONS.REFERENCE' | translate }} {{ quote.quoteRequestReferenceNo }}
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Price -->
                  <div class="text-center py-4">
                    <div class="text-3xl font-bold text-teal-600">
                      {{
                        formatCurrency(
                          quote.policyTaxablePremium || quote.policyPremium,
                          ""
                        )
                      }}
                    </div>
                    <p class="text-sm text-gray-500 mt-1">{{ 'QUOTATIONS.PRICE_BEFORE_VAT' | translate }}</p>

                    <!-- Show discount if available -->
                    <!-- <div *ngIf="getTotalDiscount(quote) > 0" class="mt-2">
                      <span class="text-xs text-green-600 font-medium">
                        {{ getDiscountPercentage(quote) }}% discount applied
                      </span>
                    </div> -->
                  </div>
                </div>

                <div
                  class="mx-6 border rounded-lg border-[#96F7E4] p-4 bg-[#F0FDFA] mb-2"
                >
                  <button
                    (click)="openQuoteDetails(quote)"
                    class="text-teal-600 hover:text-teal-700 font-medium text-sm flex items-center space-x-1 transition-colors"
                  >
                    <span>{{ 'QUOTATIONS.DETAILED_COVERAGE_INFO' | translate }}</span>
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>

                  <!-- Expanded Coverage Details -->
                </div>

                <!-- Select Button -->
                <div class="p-6 pt-0">
                  <button
                    class="w-full py-3 rounded-lg font-medium transition-colors"
                    [class.bg-teal-600]="
                      selectedQuote?.quoteResponseId !== quote.quoteResponseId
                    "
                    [class.text-white]="
                      selectedQuote?.quoteResponseId !== quote.quoteResponseId
                    "
                    [class.hover:bg-teal-700]="
                      selectedQuote?.quoteResponseId !== quote.quoteResponseId
                    "
                    [class.bg-teal-50]="
                      selectedQuote?.quoteResponseId === quote.quoteResponseId
                    "
                    [class.text-teal-600]="
                      selectedQuote?.quoteResponseId === quote.quoteResponseId
                    "
                    (click)="selectQuote(quote); $event.stopPropagation()"
                  >
                  {{
                    selectedQuote?.quoteResponseId === quote.quoteResponseId
                      ? ('QUOTATIONS.SELECTED' | translate)
                      : ('QUOTATIONS.SELECT_THIS_QUOTE' | translate)
                  }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="flex justify-between items-center pt-8 border-gray-200">
              <button
                (click)="navigateToHealthDeclaration()"
                class="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <svg
                  class="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 19l-7-7 7-7"
                  ></path>
                </svg>
                {{ 'QUOTATIONS.BACK_TO_HEALTH_DECLARATION' | translate }}
              </button>

              <button
                (click)="continueToPayment()"
                [disabled]="!selectedQuote"
                class="flex items-center px-8 py-3 rounded-lg font-medium transition-all"
                [class.bg-teal-600]="selectedQuote"
                [class.text-white]="selectedQuote"
                [class.hover:bg-teal-700]="selectedQuote"
                [class.shadow-lg]="selectedQuote"
                [class.bg-gray-200]="!selectedQuote"
                [class.text-gray-400]="!selectedQuote"
                [class.cursor-not-allowed]="!selectedQuote"
              >
                <span>{{ 'QUOTATIONS.CONTINUE_TO_PAYMENT' | translate }}</span>
                <svg
                  class="w-5 h-5 ml-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  ></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </main>
</div>

<app-quote-details-modal
  [quote]="selectedQuoteForDetails"
  [isOpen]="showQuoteDetails"
  (closeModal)="closeQuoteDetails()"
>
</app-quote-details-modal>
